import { Controller } from "@hotwired/stimulus"

// Live search / origin filter / fee sort + "show 8 at a time" pagination over
// the species list. Items are either photo cards (in cardGrid) or rows (in the
// table) — both carry the `item` target and the same data-name/origin/fee, so
// the controls act on the whole list. Pure client-side enhancement: with no JS
// every species simply shows.
export default class extends Controller {
  static targets = ["cardGrid", "tableWrap", "tableBody", "item", "search", "origin", "sort", "showMore", "count", "empty"]
  static values = { pageSize: { type: Number, default: 8 } }

  connect() {
    this.visible = this.pageSizeValue
    this.render()
  }

  // Any filter/sort change resets pagination to the first page.
  apply() {
    this.visible = this.pageSizeValue
    this.render()
  }

  showMore() {
    this.visible += this.pageSizeValue
    this.render()
  }

  render() {
    const q = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()
    const origin = this.hasOriginTarget ? this.originTarget.value : "all"
    const sort = this.hasSortTarget ? this.sortTarget.value : "featured"

    let matches = this.itemTargets.filter((el) => {
      const nameOk = !q || (el.dataset.name || "").includes(q)
      const originOk = origin === "all" || el.dataset.origin === origin
      return nameOk && originOk
    })

    if (sort === "fee_asc" || sort === "fee_desc") {
      matches.sort((a, b) => {
        const fa = this.#fee(a)
        const fb = this.#fee(b)
        if (fa === null && fb === null) return 0
        if (fa === null) return 1 // price-on-request / no fee sorts last either way
        if (fb === null) return -1
        return sort === "fee_asc" ? fa - fb : fb - fa
      })
      // Reflow each item within its own container (cards in the grid, rows in
      // the table body) to the sorted order.
      matches.forEach((el) => {
        const container = el.dataset.kind === "row" ? this.tableBodyTarget : this.cardGridTarget
        if (container) container.appendChild(el)
      })
    }

    this.itemTargets.forEach((el) => el.classList.add("d-none"))
    const shownItems = matches.slice(0, this.visible)
    shownItems.forEach((el) => el.classList.remove("d-none"))

    // Hide a container entirely when it has nothing visible (so an empty table
    // header or a stray grid gap never shows).
    if (this.hasCardGridTarget) {
      this.cardGridTarget.classList.toggle("d-none", !shownItems.some((el) => el.dataset.kind === "card"))
    }
    if (this.hasTableWrapTarget) {
      this.tableWrapTarget.classList.toggle("d-none", !shownItems.some((el) => el.dataset.kind === "row"))
    }

    const shown = shownItems.length
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("d-none", matches.length !== 0)
    if (this.hasCountTarget) {
      this.countTarget.textContent = matches.length ? `Showing ${shown} of ${matches.length}` : ""
    }
    if (this.hasShowMoreTarget) this.showMoreTarget.classList.toggle("d-none", shown >= matches.length)
  }

  #fee(el) {
    const v = parseFloat(el.dataset.fee)
    return Number.isFinite(v) ? v : null
  }
}
